/**
 * HotelbedsContentSyncPanel — admin dashboard card for the Phase 2
 * Content-API cache. Shows collection counts + freshness + a
 * "Sync Now" trigger.
 *
 * Admin-only. White/navy theme. Never customer-facing.
 */
import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Database, PlayCircle, Info } from "lucide-react";
import { api } from "@/lib/api";
import PoweredByHotelbeds from "@/components/hotels/PoweredByHotelbeds";

function relativeTime(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "never";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function HotelbedsContentSyncPanel() {
  const [status, setStatus] = useState(null);
  const [syncLog, setSyncLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/hotelbeds/content/status"),
        api.get("/admin/hotelbeds/content/sync-log?limit=5"),
      ]);
      setStatus(s.data);
      setSyncLog(l.data.entries || []);
    } catch (e) {
      console.error("[HotelbedsContentSyncPanel]", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const triggerSync = async (target = "all") => {
    setSyncing(true);
    try {
      await api.post(`/admin/hotelbeds/content/sync?target=${target}`);
      // Poll status every 3s for ~30s while the job runs.
      let ticks = 0;
      const iv = setInterval(async () => {
        ticks += 1;
        await load();
        if (ticks >= 10) {
          clearInterval(iv);
          setSyncing(false);
        }
      }, 3000);
    } catch (e) {
      console.error("[HotelbedsContentSyncPanel] sync trigger failed", e);
      setSyncing(false);
    }
  };

  return (
    <section
      data-testid="admin-hotelbeds-content-panel"
      className="rounded-2xl border-2 border-tb-cream-3 bg-white p-5"
    >
      <header className="flex flex-wrap items-center gap-3">
        <Database className="w-5 h-5 text-tb-navy" />
        <h3 className="font-serif text-tb-navy text-xl" style={{ fontFamily: '"Playfair Display", serif' }}>
          Hotelbeds Content Sync
        </h3>
        {status && (
          <span
            data-testid="admin-hotelbeds-content-enabled"
            className={
              "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-hand uppercase tracking-widest " +
              (status.sync_enabled
                ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                : "bg-tb-navy/10 text-tb-navy/70 border-tb-navy/30")
            }
          >
            Scheduler: {status.sync_enabled ? "ON" : "OFF"}
          </span>
        )}
        <button
          type="button"
          data-testid="admin-hotelbeds-content-sync-btn"
          onClick={() => triggerSync("all")}
          disabled={syncing}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-tb-navy-deep text-tb-gold-bright border-2 border-tb-gold font-hand text-xs tb-tap disabled:opacity-45"
        >
          <PlayCircle className={"w-4 h-4 " + (syncing ? "animate-pulse" : "")} />
          {syncing ? "Syncing…" : "Sync Now"}
        </button>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-tb-cream-3 hover:border-tb-navy font-hand text-tb-navy/70 text-xs tb-tap disabled:opacity-40"
        >
          <RefreshCw className={"w-3 h-3 " + (loading ? "animate-spin" : "")} /> Refresh
        </button>
      </header>

      {status?.sync_destinations?.length > 0 && (
        <p className="mt-2 font-hand text-tb-navy/60 text-xs italic flex items-center gap-1.5">
          <Info className="w-3 h-3" /> Sandbox destinations scoped to: {status.sync_destinations.join(", ")}
        </p>
      )}

      {loading ? (
        <p className="font-hand text-tb-navy/55 py-4">Loading…</p>
      ) : status ? (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {Object.entries(status.collections).map(([label, v]) => (
            <div
              key={label}
              data-testid={`admin-hotelbeds-content-${label}`}
              className="rounded-xl border border-tb-cream-3 bg-tb-cream-2 px-3 py-2"
            >
              <div className="font-hand text-tb-navy/60 text-[10px] uppercase tracking-widest">{label}</div>
              <div className="font-serif text-tb-navy text-2xl leading-none mt-0.5" style={{ fontFamily: '"Playfair Display", serif' }}>
                {v.count}
              </div>
              <div className="font-hand text-tb-navy/50 text-[10px] mt-1">
                {relativeTime(v.last_synced_at)}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {syncLog.length > 0 && (
        <div className="mt-4">
          <div className="font-hand text-tb-navy/55 text-[11px] uppercase tracking-widest mb-1">Recent sync log</div>
          <ul className="space-y-1 font-hand text-xs" data-testid="admin-hotelbeds-content-log">
            {syncLog.map((e, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className={
                  "shrink-0 inline-block w-2 h-2 rounded-full " +
                  (e.status === "ok" ? "bg-emerald-500" :
                   e.status === "partial" ? "bg-amber-500" :
                   e.status === "error" ? "bg-red-500" : "bg-tb-navy/40")
                } />
                <span className="text-tb-navy">{e.job}</span>
                <span className="text-tb-navy/45">— {e.records_synced ?? 0} rows</span>
                <span className="ml-auto text-tb-navy/40">{relativeTime(e.started_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between">
        <PoweredByHotelbeds size="xs" />
        <span className="font-hand text-tb-navy/40 text-[10px]">
          Weekly refresh Sun 03:00 UTC · daily ratecomments 04:00 UTC
        </span>
      </div>
    </section>
  );
}
